import { Circle, Group, Image, Rect, Text } from "react-konva";
import { FloorAnalyticsView } from "src/core/enums/floor-analytics-enums";
import { floorplanColors } from "src/core/enums/floorplan-color-enums";
import { FloorShape } from "src/shared/models/floorPlan";

interface AAssetRendererComponentProps {
  shape: FloorShape;
  isGateway: boolean;
  isPhotocell: boolean;
  isHighlighted: boolean;
  isSelected: boolean;
  selectedAssetOrder: Record<string, number>;
  viewType: FloorAnalyticsView;
  heatmapColor?: string;
  heatmapValue?: number | null;
  lightOPStatusColor?: string;
  gatewayImageFile: any;
  photocellImageFileOffline: any;
  selectedAssetImageFile: any;
  dynamicCircle: number;
  dynamicCirclePositionOffset: number;
  dynamicGatewaySize: number;
  dynamicGatewayOffset: number;
  dynamicGatewayLabelOffset: number;
  dynamicPhotocellSize: number;
  dynamicPhotocellOffset: number;
  dynamicPhotocellLabelOffset: number;
  showLabels: boolean;
  labelLayout?: { x: number; y: number; w: number; h: number };
  labelDynamics: {
    fontSize: number;
    height: number;
    padding: number;
    textPadding: number;
    strokeWidth: number;
    cornerRadius: number;
    shadowBlur: number;
    shadowColor: number;
    shadowOffset: number;
  };
  zoomLevel: number;
  onAssetClick: (id: string, e?: any) => void;
  onAssetMouseEnter: (e: any) => void;
  onAssetMouseLeave: (e: any) => void;
  assetScale: { x: number, y: number };
}

const AssetRendererComponent: React.FC<AAssetRendererComponentProps> = ({
  shape,
  isGateway,
  isPhotocell,
  isHighlighted,
  isSelected,
  selectedAssetOrder,
  viewType,
  heatmapColor,
  heatmapValue,
  lightOPStatusColor,
  gatewayImageFile,
  photocellImageFileOffline,
  selectedAssetImageFile,
  dynamicCircle,
  dynamicCirclePositionOffset,
  dynamicGatewaySize,
  dynamicGatewayOffset,
  dynamicGatewayLabelOffset,
  dynamicPhotocellSize,
  dynamicPhotocellOffset,
  dynamicPhotocellLabelOffset,
  showLabels,
  labelLayout,
  labelDynamics,
  zoomLevel,
  onAssetClick,
  onAssetMouseEnter,
  onAssetMouseLeave,
  assetScale
}) => {
  const orderValue = selectedAssetOrder[shape.assetCode];
  const serialNumberText = orderValue ? String(orderValue) : "";

  if (isGateway || isPhotocell) {
    const imageSize = isGateway ? dynamicGatewaySize : dynamicPhotocellSize;
    const imageOffset = isGateway ? dynamicGatewayOffset : dynamicPhotocellOffset;
    const imageLabelOffset = isGateway
      ? dynamicGatewayLabelOffset
      : dynamicPhotocellLabelOffset;

    const imageNode = (
      <Image
        name={isGateway ? "gateway-image" : "photocell-image"}
        x={shape.x + imageOffset}
        y={shape.y + imageOffset}
        image={
          isHighlighted
            ? selectedAssetImageFile
            : isGateway
              ? gatewayImageFile
              : photocellImageFileOffline
        }
        width={imageSize}
        height={imageSize}
        onClick={(e: any) => onAssetClick(shape.id, e)}
        onTap={(e: any) => onAssetClick(shape.id, e)}
        onMouseEnter={onAssetMouseEnter}
        onMouseLeave={onAssetMouseLeave}
      />
    );

    return (
      <>
        {isSelected && isPhotocell ? (
          <Group>
            <Circle
              x={shape.x + imageOffset + imageSize / 2}
              y={shape.y + imageOffset + imageSize / 2}
              radius={imageSize / 2 + 2 / zoomLevel}
              stroke="blue"
              strokeWidth={Math.max(
                0.1, 0.5 / zoomLevel + (isSelected ? 3 / zoomLevel : 0),
              )}
              listening={false}
            />
            {imageNode}
            {serialNumberText && (
              <Text
                text={serialNumberText}
                x={shape.x + imageOffset + 6 / zoomLevel}
                y={shape.y + imageOffset + imageSize - 18 / zoomLevel}
                fontStyle="bold"
                fontSize={labelDynamics.fontSize + 4 / zoomLevel}
                fill="#000"
                align="center"
                verticalAlign="bottom"
                listening={false}
              />
            )}
          </Group>
        ) : (
          imageNode
        )}

        {showLabels && labelLayout && (
          <Group
            x={labelLayout.x}
            y={labelLayout.y + imageLabelOffset}
            name="sensor-label"
            listening={false}
          >
            <Rect
              width={labelLayout.w}
              height={labelLayout.h}
              fill="#fff"
              stroke="#bdbdbd"
              strokeWidth={labelDynamics.strokeWidth}
              cornerRadius={labelDynamics.cornerRadius}
              shadowColor={`rgba(0,0,0,${labelDynamics.shadowColor})`}
              shadowBlur={labelDynamics.shadowBlur}
              shadowOffset={{
                x: labelDynamics.shadowOffset,
                y: labelDynamics.shadowOffset,
              }}
            />
            <Text
              text={shape?.labelDisplayName || shape?.sensorDisplayName || shape?.assetCode}
              fontSize={labelDynamics.fontSize}
              fill="#333"
              fontStyle="bold"
              padding={labelDynamics.textPadding}
              align="left"
            />
          </Group>
        )}
      </>
    );
  }

  // Sensor rendering
  const shouldUseSolidHeatmapFill =
    viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION &&
    (heatmapValue === null || heatmapValue === undefined);

  const heatmapCircleRadius = shouldUseSolidHeatmapFill
    ? dynamicCircle / 2.5
    : dynamicCircle / 2 +
    (isSelected && viewType === FloorAnalyticsView.ANALYTICS_VIEW
      ? dynamicCircle / 2.5
      : 0);

  const circleProps: any = {
    name: "asset-circle",
    x:
      shape.x +
      dynamicCirclePositionOffset +
      dynamicCircle / 2 -
      (viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION ? 10 : 0),
    y:
      shape.y +
      dynamicCirclePositionOffset +
      dynamicCircle / 2 -
      (viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION ? 10 : 0),
    radius: heatmapCircleRadius,
    onClick: (e: any) => onAssetClick(shape.id, e),
    onTap: (e: any) => onAssetClick(shape.id, e),
    onMouseEnter: onAssetMouseEnter,
    onMouseLeave: onAssetMouseLeave,
  };

  if (
    viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION &&
    !isHighlighted &&
    heatmapColor
  ) {
    if (shouldUseSolidHeatmapFill) {
      circleProps.fill = heatmapColor;
      circleProps.stroke = floorplanColors.border;
      circleProps.strokeWidth = Math.max(0.1, 0.5 / zoomLevel);
    } else {
      circleProps.fillRadialGradientStartPoint = { x: 0, y: 0 };
      circleProps.fillRadialGradientStartRadius = 0;
      circleProps.fillRadialGradientEndPoint = { x: 0, y: 0 };
      circleProps.fillRadialGradientEndRadius = dynamicCircle / 2;
      circleProps.fillRadialGradientColorStops = [
        0,
        heatmapColor,
        0.15,
        heatmapColor,
        1,
        "transparent",
      ];
    }
  } else if (
    viewType === FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS &&
    !isHighlighted &&
    lightOPStatusColor
  ) {
    // Light OP status view uses solid color for both On and Off states.
    circleProps.fill = lightOPStatusColor;
    circleProps.stroke = floorplanColors.border;
    circleProps.strokeWidth = Math.max(0.1, 0.5 / zoomLevel);
  } else {
    circleProps.fill = isHighlighted
      ? floorplanColors.selected
      : floorplanColors.lightOff;
    circleProps.stroke = isSelected ? "blue" : floorplanColors.border;
    circleProps.strokeWidth = Math.max(
      0.1,
      0.5 / zoomLevel + (isSelected ? 3 / zoomLevel : 0),
    );
  }

  return (
    <>
      <Circle {...circleProps} />

      {isSelected && viewType === FloorAnalyticsView.ANALYTICS_VIEW && (
        (() => {
          const fontSize = 15 / zoomLevel;
          const positionOffset = dynamicCircle / 1.1;
          const width = dynamicCircle + (dynamicCircle / 1.3);
          const height = dynamicCircle + (dynamicCircle / 1.1);


          return (
            <Text
              text={serialNumberText}
              x={circleProps.x - positionOffset}
              y={circleProps.y - positionOffset}
              width={width}
              height={height}
              align="center"
              verticalAlign="middle"
              fontSize={fontSize}
              fontStyle="bold"
              fill="#1f1f1f"
              listening={false}
            />
          );
        })()
      )}

      {showLabels && labelLayout && (
        <Group
          x={
            labelLayout.x -
            (viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION
              ? 10
              : 0)
          }
          y={
            labelLayout.y -
            (viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION
              ? 10
              : 0)
          }
          name="sensor-label"
          listening={false}
        >
          <Rect
            width={labelLayout.w}
            height={labelLayout.h}
            fill="#fff"
            stroke="#bdbdbd"
            strokeWidth={labelDynamics.strokeWidth}
            cornerRadius={labelDynamics.cornerRadius}
            shadowColor={`rgba(0,0,0,${labelDynamics.shadowColor})`}
            shadowBlur={labelDynamics.shadowBlur}
            shadowOffset={{
              x: labelDynamics.shadowOffset,
              y: labelDynamics.shadowOffset,
            }}
          />
          <Text
            text={shape?.labelDisplayName || shape?.sensorDisplayName || shape?.assetCode}
            fontSize={labelDynamics.fontSize}
            fill="#333"
            fontStyle="bold"
            padding={labelDynamics.textPadding}
            align="center"
          />
        </Group>
      )}
    </>
  );
};

export default AssetRendererComponent;
